import { Api } from 'src/apis/api';
import { FunnelController } from 'src/controllers/business/FunnelController';
import { UiController } from 'src/controllers/ui/UiController';
import { IProduct } from 'src/types/product';
import { calculatePrices } from 'src/utils/numbers';
import { createUrl } from 'src/utils/url';

interface IKlarnaAuthorizeResponse {
  approved: boolean;
  authorization_token: string;
  finalize_required: boolean;
  show_form: boolean;
}

interface IKlarnaAuthResponse {
  authorizationToken: string;
  approved: boolean;
  error: boolean;
}

export class KlarnaService {
  private fc: FunnelController;
  private containerId: string;
  private klarnaPayments: any; // TODO: create global declaration

  constructor(fc: FunnelController, containerId: string) {
    this.fc = fc;
    this.klarnaPayments = window.Klarna.Payments;
    this.containerId = containerId;
  }

  initSession = async (country?: string) => {
    const lead = this.fc.lead.lead;
    const locale = this.fc.config.locale;
    const product = this.fc.getSelectedProduct();

    try {
      if (
        this.fc.config.klarnaEnabled &&
        typeof window.Klarna?.Payments === 'undefined'
      ) {
        throw new Error('Klarna enabled but not defined');
      }
      const { unitPrice, totalAmount } = calculatePrices(
        product.finalPrice,
        product.quantity,
        product.shippingPrice || 0,
      );

      const res = await Api.getKlarnaPaymentSession({
        client: window.BRAND,
        intent: 'buy_and_tokenize',
        purchase_country: country || window.GEO.country,
        purchase_currency: product.currencyCode,
        locale: locale || 'en',
        order_amount: totalAmount,
        order_tax_amount: 0,
        order_lines: [
          {
            name: product.name,
            quantity: product.quantity,
            unit_price: unitPrice,
            tax_rate: 0,
            total_amount: unitPrice * Number(product.quantity),
            total_tax_amount: 0,
          },
          {
            name: 'Shipping Standard',
            quantity: 1,
            reference: 'shipping-standard',
            tax_rate: 0,
            total_amount: Number(product.shippingPrice) * 100,
            total_tax_amount: 0,
            unit_price: Number(product.shippingPrice) * 100,
          },
        ],
      });

      if (window.DEBUG) {
        console.log(res);
      }

      if (res?.client_token) {
        window?.Klarna?.Payments?.init({
          client_token: res?.client_token,
        });

        window.Klarna.Payments.load({
          container: this.containerId,
          payment_method_category: 'klarna',
        });

        UiController.showAvailablePaymentMethods(res.payment_method_categories);
      }
    } catch (e) {
      Errors.report(e, {
        lead: lead || 'undefined',
        product: product || 'undefined',
        config: this.fc.config || 'undefined',
      });
    }
  };

  private createKlarnaOrderObj = (product: IProduct) => {
    const redirect = createUrl(this.fc.config.redirectTo || '', {
      prospect_id: String(this.fc.lead.lead.orderId),
    });
    const { unitPrice, totalAmount } = calculatePrices(
      product.finalPrice,
      product.quantity,
      product.shippingPrice || 0,
    );

    return {
      client: window.BRAND,
      authorizationToken: '',
      intent: 'buy_and_tokenize',
      purchase_country: this.fc.lead.lead.country,
      purchase_currency: product.currencyCode,
      locale: this.fc.config.locale || 'en',
      shipping_address: {
        given_name: this.fc.lead.lead.firstName,
        family_name: this.fc.lead.lead.lastName,
        email: this.fc.lead.lead.emailAddress,
        street_address: this.fc.lead.lead.address1,
        postal_code: this.fc.lead.lead.postalCode,
        city: this.fc.lead.lead.city,
        region: this.fc.lead.lead.state,
        phone: this.fc.lead.lead.phoneNumber,
        country: this.fc.lead.lead.country,
      },
      order_amount: totalAmount,
      order_tax_amount: 0,
      order_lines: [
        {
          name: product.name,
          quantity: product.quantity,
          unit_price: unitPrice,
          tax_rate: 0,
          total_amount: unitPrice * Number(product.quantity),
          total_tax_amount: 0,
          image_url: product.image,
        },
        {
          name: 'Shipping Standard',
          quantity: 1,
          reference: 'shipping-standard',
          tax_rate: 0,
          total_amount: Number(product.shippingPrice) * 100,
          total_tax_amount: 0,
          unit_price: Number(product.shippingPrice) * 100,
        },
      ],
      merchant_urls: {
        confirmation: redirect,
        checkout: redirect,
      },
    };
  };

  authorizeOrder = async (product: IProduct) => {
    const order = this.createKlarnaOrderObj(product);

    const resultPromise = new Promise<IKlarnaAuthResponse>(resolve => {
      this.klarnaPayments.authorize(
        { payment_method_category: 'klarna' },
        order,
        (res: IKlarnaAuthorizeResponse) => {
          resolve({
            approved: res.approved,
            authorizationToken: res?.authorization_token,
            error: res?.authorization_token === 'undefined',
          });
        },
      );
    });
    const { authorizationToken, error, approved } = await resultPromise;
    if (error) {
      throw new Error('Klarna not authorized order');
    }

    order.authorizationToken = authorizationToken;
    const klarnaApproved = approved;
    return { klarnaApproved, order };
  };

  createExpressKlarnaOrderObj = (
    product: IProduct,
    authToken?: string,
    collected_shipping_address?: any,
  ) => {
    const { unitPrice, totalAmount } = calculatePrices(
      product.finalPrice,
      product.quantity,
      product.shippingPrice || 0,
    );
    const orderPayload: any = {
      client: window.BRAND,
      intent: 'buy_and_tokenize',
      locale: this.fc.config.locale || 'en',
      order_amount: totalAmount,
      order_lines: [
        {
          name: product.name,
          quantity: product.quantity,
          unit_price: unitPrice,
          tax_rate: 0,
          total_amount: unitPrice * Number(product.quantity),
          total_tax_amount: 0,
          image_url: product.image,
        },
        {
          name: 'Shipping Standard',
          quantity: 1,
          reference: 'shipping-standard',
          tax_rate: 0,
          total_amount: Number(product.shippingPrice) * 100,
          total_tax_amount: 0,
          unit_price: Number(product.shippingPrice) * 100,
        },
      ],
      order_tax_amount: 0,
      purchase_country: this.fc.lead.lead.country,
      purchase_currency: product.currencyCode,
    };
    if (authToken) {
      orderPayload.authorizationToken = authToken;
    }
    if (collected_shipping_address) {
      orderPayload.shipping_address = collected_shipping_address;
      orderPayload.purchase_country = collected_shipping_address.country;
    }
    return orderPayload;
  };
}
